import React, { useEffect, useRef } from 'react';

export default function MicVisualizer({
  active,
  voiceActivity = 0,
  isSilent = false,
  mediaStream = null,
  width = 90,
  height = 90,
  color = '#fff',
  barWidth = 8,
  numBars = 4,
}) {
  const canvasRef = useRef(null);
  const colorRef = useRef({ r: 255, g: 255, b: 255 });
  const rafRef = useRef(null);
  const barHeightsRef = useRef(new Array(numBars).fill(0));
  // Base variation range for each bar (static component)
  const barBaseVariationsRef = useRef(new Array(numBars).fill(1).map(() => 0.7 + Math.random() * 0.7));
  // Phase offsets for independent oscillation (0 to 2π)
  const barPhaseOffsetsRef = useRef(new Array(numBars).fill(1).map(() => Math.random() * Math.PI * 2));
  // Different oscillation frequencies for each bar (Hz)
  const barFrequenciesRef = useRef(new Array(numBars).fill(1).map(() => 0.8 + Math.random() * 0.7)); // 0.8-1.5 Hz
  // Different response speeds for each bar
  const barRiseSpeedsRef = useRef(new Array(numBars).fill(1).map(() => 0.3 + Math.random() * 0.15)); // 0.3-0.45
  const barFallSpeedsRef = useRef(new Array(numBars).fill(1).map(() => 0.06 + Math.random() * 0.03)); // 0.06-0.09
  const peakHoldTimersRef = useRef(new Array(numBars).fill(0));
  const lastTsRef = useRef(0);
  const startTimeRef = useRef(performance.now());

  // Parse color on mount and when color changes
  useEffect(() => {
    const hex = color.startsWith('#') ? color.slice(1) : color;
    if (hex.length === 3) {
      const r = parseInt(hex[0] + hex[0], 16);
      const g = parseInt(hex[1] + hex[1], 16);
      const b = parseInt(hex[2] + hex[2], 16);
      colorRef.current = { r, g, b };
    } else if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      colorRef.current = { r, g, b };
    }
  }, [color]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (!active) {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, width, height);
      }
      return;
    }

    const centreY = height / 2;
    const barSpacing = 6;
    const barsCount = numBars;
    const totalBarsWidth = (barsCount * barWidth) + ((barsCount - 1) * barSpacing);
    const startX = (width - totalBarsWidth) / 2;

    const minHeight = height * 0.12; // Slightly smaller minimum for more dynamic range
    const maxHeight = height * 0.85; // Increased from 0.7 to 0.85 for taller bars

    const peakHoldMs = 220; // brief peak hold to make bars last longer

    function draw(ts) {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.clearRect(0, 0, width, height);

      const dt = lastTsRef.current ? Math.min(100, ts - lastTsRef.current) : 16;
      lastTsRef.current = ts || 0;

      // Calculate elapsed time in seconds for oscillation
      const elapsedTime = (ts - startTimeRef.current) / 1000;

      // Apply exponential scaling with aggressive amplification
      // For Capacitor: voiceActivity is abs(dB) typically 0-60 range
      // For Web: voiceActivity is 0-100 range
      // Normalize to 0-1 but amplify the signal more aggressively
      const activityNormalized = Math.min(1, voiceActivity / 60); // Assume 60 dB max for better scaling

      // Use power curve for more punch: lower exponent = more aggressive response
      const exponentialScale = Math.pow(activityNormalized, 0.45); // Changed from 0.6 to 0.45 for more sensitivity
      const baseHeight = minHeight + (maxHeight - minHeight) * exponentialScale;

      for (let i = 0; i < barsCount; i++) {
        // Calculate dynamic variation for this bar using time-based oscillation
        const frequency = barFrequenciesRef.current[i];
        const phase = barPhaseOffsetsRef.current[i];
        const baseVariation = barBaseVariationsRef.current[i];

        // Create oscillation with unique phase and frequency for each bar
        // Oscillation amplitude scales with sound activity (more sound = more movement)
        const oscillationAmplitude = 0.15 * (0.3 + 0.7 * activityNormalized); // 0.045-0.15 range
        const oscillation = Math.sin(elapsedTime * frequency * Math.PI * 2 + phase) * oscillationAmplitude;

        // Dynamic variation combines base + oscillation
        const dynamicVariation = baseVariation + oscillation;

        // Calculate target height with dynamic variation
        // When silent, bars still oscillate gently but stay small
        const targetHeight = isSilent
          ? minHeight * (0.3 + oscillation * 0.2) // Gentle bobbing when silent
          : baseHeight * dynamicVariation;

        // Use per-bar response speeds for independent movement timing
        const riseLerp = barRiseSpeedsRef.current[i];
        const fallLerp = barFallSpeedsRef.current[i];

        // Directional smoothing with brief peak hold for longer persistence
        const currentHeight = barHeightsRef.current[i];
        const goingDown = targetHeight < currentHeight - 0.5;
        if (!goingDown) {
          // Rising or stable → react quickly and refresh peak hold
          peakHoldTimersRef.current[i] = peakHoldMs;
          barHeightsRef.current[i] = currentHeight + (targetHeight - currentHeight) * riseLerp;
        } else {
          // Falling → hold peak for a short while, then decay smoothly
          const timeLeft = Math.max(0, (peakHoldTimersRef.current[i] || 0) - dt);
          peakHoldTimersRef.current[i] = timeLeft;
          const effectiveTarget = timeLeft > 0 ? currentHeight : targetHeight;
          barHeightsRef.current[i] = currentHeight + (effectiveTarget - currentHeight) * fallLerp;
        }
        const barHeight = barHeightsRef.current[i];

        // Calculate x position for this bar
        const x = startX + i * (barWidth + barSpacing) + barWidth / 2;

        // Draw rounded rectangle bar
        const y = centreY - barHeight / 2;
        const radius = barWidth / 2;

        // Always draw solid white bars (no change based on silence)
        ctx.fillStyle = `rgb(${colorRef.current.r}, ${colorRef.current.g}, ${colorRef.current.b})`;
        ctx.strokeStyle = `rgb(${colorRef.current.r}, ${colorRef.current.g}, ${colorRef.current.b})`;
        ctx.lineWidth = 1;

        ctx.beginPath();

        // Check if bar is tall enough for rounded corners
        if (barHeight > barWidth) {
          // Top rounded cap
          ctx.arc(x, y + radius, radius, Math.PI, 0, false);
          // Right edge
          ctx.lineTo(x + radius, y + barHeight - radius);
          // Bottom rounded cap
          ctx.arc(x, y + barHeight - radius, radius, 0, Math.PI, false);
          // Left edge
          ctx.lineTo(x - radius, y + radius);
        } else {
          // If bar is too short, just draw a circle
          ctx.arc(x, centreY, barHeight / 2, 0, Math.PI * 2, false);
        }

        ctx.closePath();
        ctx.fill();

        // Enhanced glow for more visual punch
        ctx.shadowColor = `rgba(${colorRef.current.r}, ${colorRef.current.g}, ${colorRef.current.b}, 0.6)`;
        ctx.shadowBlur = 5; // Increased from 3 to 5 for more glow
        ctx.stroke();
        ctx.shadowBlur = 0;
      }

      rafRef.current = requestAnimationFrame(draw);
    }

    rafRef.current = requestAnimationFrame(draw);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [active, width, height, barWidth, numBars, voiceActivity, isSilent]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{
        display: 'block',
        width,
        height,
        pointerEvents: 'none',
        backgroundColor: 'transparent',
      }}
    />
  );
}
